import { motion } from "framer-motion";
import { Inbox, BrainCircuit, Cable, GitBranch, Play, UserCheck, Trophy, ShoppingBag, Newspaper, Megaphone, UserPlus, FileScan, Headset, ClipboardList, CalendarClock, BarChart2, DatabaseZap } from "lucide-react";
import { SectionHeading, FadeUp } from "./Reveal";

const STEPS = [
  { label: "Request", Icon: Inbox },
  { label: "AI understands", Icon: BrainCircuit },
  { label: "Systems accessed", Icon: Cable },
  { label: "Decision", Icon: GitBranch },
  { label: "Action", Icon: Play },
  { label: "Human approval", Icon: UserCheck },
  { label: "Outcome", Icon: Trophy },
];

const EXAMPLES = [
  { label: "Purchase order automation", Icon: ShoppingBag },
  { label: "Content workflows", Icon: Newspaper },
  { label: "Marketing workflows", Icon: Megaphone },
  { label: "Customer onboarding", Icon: UserPlus },
  { label: "Document processing", Icon: FileScan },
  { label: "Support operations", Icon: Headset },
  { label: "Registration operations", Icon: ClipboardList },
  { label: "Scheduling", Icon: CalendarClock },
  { label: "Reporting", Icon: BarChart2 },
  { label: "Data processing", Icon: DatabaseZap },
];

export const AutomationFlow = () => (
  <section data-testid="automation-flow-section" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <SectionHeading
        testId="automation-flow"
        overline="Automation & Operations"
        title={<>Automate the work <span className="jx-gradient-text">behind the work.</span></>}
        body="Every automated workflow follows a governed path — from request to outcome, with human judgment exactly where it matters."
      />

      <FadeUp delay={0.2} className="mt-16">
        <div data-testid="workflow-animation" className="rounded-3xl bg-white border border-jx-ink/8 p-6 sm:p-10 overflow-x-auto">
          <div className="flex items-center min-w-[900px]">
            {STEPS.map((s, i) => (
              <div key={s.label} className="flex items-center flex-1 last:flex-none">
                <motion.div
                  initial={{ opacity: 0, y: 24, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.18, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="flex flex-col items-center gap-3 w-28 shrink-0"
                  data-testid={`workflow-step-${i}`}
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-300 hover:scale-110 ${i === STEPS.length - 1 ? "jx-gradient-bg shadow-[0_12px_30px_rgba(209,45,140,0.3)]" : "bg-jx-mist border border-jx-ink/8"}`}>
                    <s.Icon className={`w-6 h-6 ${i === STEPS.length - 1 ? "text-white" : "text-jx-purple"}`} strokeWidth={1.8} />
                  </div>
                  <p className="text-[11px] font-bold tracking-wider uppercase text-jx-ink/60 text-center leading-tight">{s.label}</p>
                </motion.div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-px relative mx-1 mb-8">
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.18 + 0.25, duration: 0.5, ease: "easeOut" }}
                      className="h-full jx-gradient-bg origin-left opacity-50"
                    />
                    <motion.div
                      animate={{ x: ["0%", "100%"] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: "linear", delay: i * 0.22 }}
                      className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-jx-magenta shadow-[0_0_10px_rgba(209,45,140,0.8)]"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </FadeUp>

      <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {EXAMPLES.map((ex, i) => (
          <FadeUp key={ex.label} delay={i * 0.05}>
            <div
              data-testid={`automation-example-${i}`}
              className="flex items-center gap-3 rounded-xl border border-jx-ink/8 bg-white px-4 py-3.5 hover:border-jx-copper/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-300"
            >
              <ex.Icon className="w-4 h-4 text-jx-copper shrink-0" strokeWidth={1.8} />
              <span className="text-xs sm:text-sm font-medium text-jx-ink/75">{ex.label}</span>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);
